/**
 * reglages-de-champ.mjs, LES REGLAGES D'UN TYPE DE CHAMP vus par le Studio
 * (`docs/socle/champs-types.md`).
 *
 * CE QU'IL FAIT. Les types de champ ont des reglages a eux (l'unite d'un
 * nombre, la devise d'un prix, la jauge d'un pourcentage, les liens multiples
 * d'un champ lie, la note maximale, la formule d'un calcul, le modele d'un
 * numero). Le brief les saisit dans des cases, donc en
 * TEXTE ; le fichier de champs les porte en nombres, en codes et en objets. Ce
 * module est le SEUL passage d'une forme a l'autre, dans les deux sens : la
 * derivation (`derive.mjs`) l'appelle pour ecrire `taki.fields.json`, la
 * reprise (`reprise.mjs`) pour relire, et l'ecran pour dire ce qu'il refuse.
 *
 * CE QU'IL NE FAIT PAS : il ne recopie aucune regle. Quel type accepte quel
 * reglage, c'est le catalogue (`accepteReglage`) ; ce qu'une formule cite et
 * refuse, c'est `formule.ts` ; les bornes d'un reglage, c'est le schema
 * (`custom-field.ts`). Ici on ne fait que LIRE une case et la poser, ou la
 * laisser vide quand elle vaut le defaut du socle.
 *
 * IL NE DEPEND PAS DE `derive.mjs` : la derivation l'importe, l'inverse ferait
 * un cycle. Ce qui a besoin de la table nature -> type la passe en argument.
 */
import {
  NOTE_MAX_PAR_DEFAUT,
  REGLAGES_DE_CHAMP,
  accepteReglage,
  analyserFormule,
  canoniserFormule,
  champsCites,
  deviseDe,
  estAutomatique,
  estNumerique,
  ficheDuType,
  formaterNumero,
  modeleDeNumeroValide,
  natureDuType,
  verifierFormule,
} from '@taki/data-model';

/**
 * LES DEVISES OFFERTES par la liste deroulante, l'euro en tete (le defaut du
 * socle). Une liste COURTE, decision du 27/10/2026 : un client MyTaki facture
 * en euros, parfois en francs suisses, en livres ou en dollars ; deux cents
 * codes ISO noieraient les quatre utiles. Une devise lue d'un fichier qui n'est
 * pas ici reste offerte a cote (`devisesPour`) : on ne la perd pas en
 * l'affichant.
 */
export const DEVISES_OFFERTES = Object.freeze(['EUR', 'CHF', 'GBP', 'USD', 'CAD']);

/** Les devises a offrir pour un champ : la liste courte, plus la sienne si elle n'y est pas. @param {unknown} devise */
export function devisesPour(devise) {
  const sienne = String(devise ?? '').trim().toUpperCase();
  return sienne !== '' && !DEVISES_OFFERTES.includes(sienne) ? [...DEVISES_OFFERTES, sienne] : [...DEVISES_OFFERTES];
}

/** Les bornes que le schema pose (`reglagesDeChamp`, `custom-field.ts`), pour borner une saisie. */
const BORNES = Object.freeze({
  decimales: [0, 6],
  max: [3, 10],
  chiffres: [1, 12],
});

const texte = (v) => (typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v));

/** Un entier saisi dans ses bornes, ou `undefined` (vide, illisible, hors bornes). */
function entierSaisi(valeur, [min, max]) {
  const brut = texte(valeur).trim();
  if (!/^\d+$/.test(brut)) return undefined;
  const n = Number(brut);
  return n >= min && n <= max ? n : undefined;
}

/**
 * LES REGLAGES D'UN CHAMP DU BRIEF, dans la forme du fichier de champs, pour le
 * type que sa nature produit. Seuls les reglages que ce type accepte s'ecrivent
 * (le catalogue) ; un reglage VIDE ou egal au DEFAUT du socle ne s'ecrit pas
 * (« le defaut s'ecrit par une absence », comme `required` ou `placeholder`).
 *
 * LA DEVISE D'UN PRIX S'ECRIT SUR LE CHAMP, EUR compris. Un `prix` est un
 * nombre habille `euros` (`habillageDeNature`), et la case vide vaut la devise
 * que cet habillage dit (`deviseDe` du data-model, EUR) : elle s'ecrit quand
 * meme. L'habillage vit sur une COLONNE de l'ecran, et un champ neuf n'en a pas
 * tant qu'aucune vue ne le cite : le Studio d'une org, qui relit sa declaration
 * a chaque ouverture, en refaisait un nombre nu (« 186 » sans €, recette du
 * 27/10/2026). Avec sa devise, le champ dit seul qu'il est un montant, a la
 * fiche, a la reprise (`natureDuNombre`) et au serveur (le fil d'une fiche, Lia,
 * le MCP), qui n'ont pas la colonne.
 *
 * SAUF L'EXISTANT : un nombre que sa colonne habille DEJA `euros` sur le disque
 * (`champ.habillage`, relu par la reprise) est un montant en EUR tel quel, et sa
 * declaration ne gagne pas de reglage (regle du fondateur du 27/10/2026, « ce
 * qui est declare se garde », `docs/socle/champs-types.md`). Un nombre sans
 * habillage, ou un CALCUL, n'a pas de devise par defaut (sans elle, c'est un
 * nombre nu). Un calcul ne prend pas une devise ET une unite (le schema le
 * refuse) : la devise l'emporte, l'ecran ne laisse pas choisir les deux.
 *
 * LA JAUGE (`affichage: 'jauge'`) et LES LIENS MULTIPLES (`plusieurs: true`)
 * s'ecrivent quand le brief les porte, et leur absence vaut le defaut : un
 * pourcentage en chiffres, un lien vers une seule fiche.
 *
 * LE MODELE D'UN NUMERO s'ecrit TEL QUE TAPE, meme sans `{numéro}` : le schema
 * le refuse alors, et c'est ce refus qui empeche d'enregistrer un numero qui ne
 * numerote rien (le formulaire le dit avant, `fautesDeReglage`). Le corriger en
 * silence aurait ecrit un autre numero que celui qu'on a demande.
 *
 * La formule n'est pas ici : elle se lit contre les champs du jeu
 * (`formuleDeclaree`).
 *
 * @param {Record<string, unknown>} champ un champ du brief
 * @param {string} type le type que sa nature produit
 * @param {string} [habillage] l'habillage de sa colonne (`habillageDeChamp`, `derive.mjs`)
 * @returns {Record<string, unknown>}
 */
export function reglagesDeclares(champ, type, habillage = '') {
  const sortie = {};
  const saisie = texte(champ?.devise).trim().toUpperCase();
  const parSaColonne = texte(champ?.habillage).trim() !== '';
  const devise = saisie !== '' || type !== 'number' || parSaColonne ? saisie : deviseDe({ type }, habillage || undefined) ?? '';
  if (accepteReglage(type, 'devise') && devise !== '') sortie.devise = devise;
  if (accepteReglage(type, 'affichage') && champ?.affichage === 'jauge') sortie.affichage = 'jauge';
  if (accepteReglage(type, 'plusieurs') && champ?.plusieurs === true) sortie.plusieurs = true;
  const unite = texte(champ?.unite).trim();
  if (accepteReglage(type, 'unite') && unite !== '' && !(type === 'calcul' && sortie.devise !== undefined)) {
    sortie.unite = unite;
  }
  const decimales = entierSaisi(champ?.decimales, BORNES.decimales);
  if (accepteReglage(type, 'decimales') && decimales !== undefined) sortie.decimales = decimales;
  const max = entierSaisi(champ?.max, BORNES.max);
  if (accepteReglage(type, 'max') && max !== undefined && max !== NOTE_MAX_PAR_DEFAUT) sortie.max = max;
  if (accepteReglage(type, 'sequence')) {
    const modele = texte(champ?.modele).trim();
    const chiffres = entierSaisi(champ?.chiffres, BORNES.chiffres);
    if (modele !== '' || chiffres !== undefined) {
      sortie.sequence = { modele: modele === '' ? '{numéro}' : modele, ...(chiffres === undefined ? {} : { chiffres }) };
    }
  }
  return sortie;
}

/** Les bornes du nombre de chiffres d'un numero (le schema), pour la case a increment. */
export const BORNES_DES_CHIFFRES = /** @type {readonly [number, number]} */ (/** @type {unknown} */ (BORNES.chiffres));

/**
 * LE PROCHAIN NUMERO TEL QU'IL SORTIRA, pour l'exemple en direct du Studio
 * (« Hallo-0000000897-Test ») : la sequence que la derivation ECRIRAIT pour ce
 * champ (`reglagesDeclares`, donc les memes defauts et les memes bornes),
 * formatee par la regle du socle (`formaterNumero`). Il suit la saisie, meme
 * pas encore enregistree. `null` quand le modele ne numerote rien (sans
 * `{numéro}`) : le refus sous la case le dit deja.
 *
 * @param {Record<string, unknown>} champ un champ du brief
 * @param {number} prochain le numero a formater
 * @param {Date | string} date le jour de l'annee a poser (`AAAA-MM-JJ` ou un instant)
 * @returns {string | null}
 */
export function exempleDeNumero(champ, prochain, date) {
  const modele = texte(champ?.modele).trim();
  if (modele !== '' && !modeleDeNumeroValide(modele)) return null;
  const sequence = /** @type {{ modele: string, chiffres?: number } | undefined} */ (reglagesDeclares(champ, 'numero').sequence);
  return formaterNumero(sequence, prochain, date);
}

/**
 * LES REGLAGES D'UN CHAMP DU FICHIER, dans la forme du brief : la lecture
 * inverse de `reglagesDeclares`, que la reprise pose sur le champ. Un reglage
 * absent du fichier est ABSENT du resultat (et la reprise le retire du champ) :
 * le brief dit alors le defaut du socle, comme le fichier.
 *
 * @param {Record<string, unknown>} declare un champ de `taki.fields.json`
 * @returns {Record<string, string | boolean>}
 */
export function reglagesDuBrief(declare) {
  /** @type {Record<string, string | boolean>} */
  const sortie = {};
  if (declare?.affichage === 'jauge') sortie.affichage = 'jauge';
  if (declare?.plusieurs === true) sortie.plusieurs = true;
  for (const clef of ['unite', 'devise', 'formule']) {
    if (typeof declare?.[clef] === 'string' && declare[clef] !== '') sortie[clef] = declare[clef];
  }
  for (const clef of ['decimales', 'max']) {
    if (typeof declare?.[clef] === 'number') sortie[clef] = String(declare[clef]);
  }
  const sequence = declare?.sequence;
  if (sequence !== null && typeof sequence === 'object') {
    if (typeof sequence.modele === 'string') sortie.modele = sequence.modele;
    if (typeof sequence.chiffres === 'number') sortie.chiffres = String(sequence.chiffres);
  }
  return sortie;
}

/** Les clefs du brief que `reglagesDuBrief` pose : la reprise retire celles que le fichier ne porte plus. */
export const CLEFS_DE_REGLAGE_DU_BRIEF = Object.freeze(['unite', 'devise', 'affichage', 'plusieurs', 'formule', 'decimales', 'max', 'modele', 'chiffres']);

/**
 * LA FORMULE A ECRIRE, lue contre les champs DECLARES du jeu : la forme
 * stockee (`{prix} * 0.25`) quand elle se lit, la saisie telle quelle sinon.
 * Telle quelle, et pas retiree : une formule illisible doit REFUSER
 * l'enregistrement (le schema la juge, `verifierFormule`), pas disparaitre en
 * laissant croire que le calcul est pose. Vide : `undefined` (le schema exige
 * alors une formule, et le formulaire l'a deja dit).
 *
 * @param {unknown} saisie @param {readonly { key: string, label?: string, type: string }[]} champs
 * @returns {string | undefined}
 */
export function formuleDeclaree(saisie, champs) {
  const brut = texte(saisie).trim();
  if (brut === '') return undefined;
  const lue = canoniserFormule(brut, champs);
  return 'formule' in lue ? lue.formule : brut;
}

/**
 * CE QUI EMPECHE D'ENREGISTRER UN CALCUL, en une phrase par faute, contre les
 * champs declares du jeu : les refus de `canoniserFormule` (nom inconnu,
 * syntaxe) puis de `verifierFormule` (nature, boucle). Vide : la formule tient.
 * C'est la phrase que l'ecran pose SOUS la case, a chaque frappe.
 *
 * @param {unknown} saisie
 * @param {{ key: string, label?: string, type: string }} champ le calcul lui-meme
 * @param {readonly { key: string, label?: string, type: string, formule?: string }[]} champs
 * @returns {string[]}
 */
export function fautesDeFormule(saisie, champ, champs) {
  const brut = texte(saisie).trim();
  if (brut === '') return ['Écrire la formule du calcul.'];
  /* LA SYNTAXE D'ABORD, avec la place de la faute : « il manque une ) » seul
     ne dit pas de quoi il manque. */
  const analysee = analyserFormule(brut);
  if ('erreur' in analysee) return [phrase(`la formule est illisible : ${analysee.erreur} (caractère ${analysee.position + 1})`)];
  const lue = canoniserFormule(brut, champs);
  if ('erreur' in lue) return [phrase(lue.erreur)];
  return verifierFormule(lue.formule, champ, champs).map(phrase);
}

/** Une faute du data-model, en phrase d'ecran : la majuscule et le point. */
function phrase(faute) {
  const t = texte(faute).trim();
  if (t === '') return t;
  const debut = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(debut) ? debut : `${debut}.`;
}

/**
 * LES CHAMPS QU'UNE FORMULE PEUT CITER, dans l'ordre du jeu : ceux dont la
 * nature est un nombre (le catalogue, `estNumerique`), sauf le calcul lui-meme.
 * C'est la liste cliquable sous la case : un clic insere la reference.
 *
 * @param {string} clefDuCalcul
 * @param {readonly { key: string, label?: string, type: string }[]} champs
 */
export function champsCitables(clefDuCalcul, champs) {
  return champs.filter((c) => c.key !== clefDuCalcul && estNumerique(c.type));
}

/**
 * LA REFERENCE QU'UN CLIC INSERE : le libelle nu quand c'est un seul mot sans
 * crochet, entre crochets sinon (« [Prime annuelle] »), la clef entre accolades
 * en dernier recours. C'est l'ecriture que `formuleLisible` rend, donc ce qu'on
 * insere se relit pareil.
 *
 * @param {{ key: string, label?: string }} champ
 */
export function referenceInseree(champ) {
  const mot = texte(champ.label).trim();
  if (/^[\p{L}_][\p{L}\p{N}_]*$/u.test(mot)) return mot;
  if (mot !== '' && !mot.includes(']')) return `[${mot}]`;
  return `{${champ.key}}`;
}

/**
 * LES CALCULS QUI CITENT UN CHAMP, par leur libelle : ceux du jeu dont la
 * formule nomme cette clef. C'est ce qui interdit de retirer, de masquer ou de
 * faire changer de nature un champ dont un calcul depend, avec le NOM du calcul
 * dans le refus.
 *
 * @param {string} clef
 * @param {readonly { key: string, label?: string, type: string, formule?: string }[]} champs
 * @returns {string[]}
 */
export function calculsQuiCitent(clef, champs) {
  return champs
    .filter((c) => c.type === 'calcul' && c.key !== clef && typeof c.formule === 'string'
      && champsCites(c.formule, champs).includes(clef))
    .map((c) => texte(c.label) || c.key);
}

const nomme = (noms) => noms.map((n) => `« ${n} »`).join(', ');

/**
 * POURQUOI UN CHAMP NE SE RETIRE PAS (ni ne se masque) : un calcul le cite.
 * `null` : rien ne l'en empeche de ce cote-ci.
 *
 * @param {string} clef @param {readonly { key: string, label?: string, type: string, formule?: string }[]} champs
 * @param {'retirer' | 'masquer'} geste
 */
export function refusDeRetrait(clef, champs, geste = 'retirer') {
  const calculs = calculsQuiCitent(clef, champs);
  if (calculs.length === 0) return null;
  const verbe = geste === 'masquer' ? 'masquer' : 'retirer';
  return `${calculs.length === 1 ? 'Le calcul' : 'Les calculs'} ${nomme(calculs)} ${calculs.length === 1 ? 'cite' : 'citent'} ce champ dans sa formule : le retirer de la formule avant de le ${verbe}.`;
}

/**
 * POURQUOI UN CHAMP NE CHANGE PAS DE TYPE : un calcul le cite, et le type
 * choisi n'est pas un nombre (le catalogue, `estNumerique`). `null` sinon.
 *
 * @param {string} clef @param {string} type le type choisi
 * @param {readonly { key: string, label?: string, type: string, formule?: string }[]} champs
 */
export function refusDeType(clef, type, champs) {
  if (estNumerique(type)) return null;
  const calculs = calculsQuiCitent(clef, champs);
  if (calculs.length === 0) return null;
  const genre = ficheDuType(type)?.genre ?? type;
  return `${calculs.length === 1 ? 'Le calcul' : 'Les calculs'} ${nomme(calculs)} ${calculs.length === 1 ? 'cite' : 'citent'} ce champ : il doit rester un nombre, pas un champ ${genre}.`;
}

/** Le pluriel de chaque forme de valeur (`nature` du catalogue), pour dire ce que deviennent les fiches. */
const FORMES = Object.freeze({
  texte: 'des textes',
  nombre: 'des nombres',
  booleen: 'des oui / non',
  liste: 'des listes',
  date: 'des dates',
  dateheure: 'des dates et heures',
  periode: 'des périodes',
});

/**
 * CE QUE DEVIENNENT LES FICHES DEJA SAISIES quand un champ change de type, en
 * une ligne, ou `null` quand rien ne change pour elles. La valeur stockee ne
 * bouge pas (un changement de type n'est pas une migration) : si sa FORME
 * change (le catalogue, `natureDuType`), l'ecran ne la relira plus. Un type
 * dont le socle pose la valeur (calcul, numero) remplace ce qu'on saisissait.
 *
 * @param {string} avant le type d'avant @param {string} apres le type choisi
 */
export function avertissementDeType(avant, apres) {
  if (avant === apres || avant === '' || apres === '') return null;
  if (estAutomatique(apres) && !estAutomatique(avant)) {
    return apres === 'calcul'
      ? 'La valeur sera calculée par la formule : ce qui était saisi sera remplacé.'
      : 'Le numéro sera attribué par le socle : ce qui était saisi sera remplacé.';
  }
  const formeAvant = natureDuType(avant);
  const formeApres = natureDuType(apres);
  if (formeAvant === undefined || formeApres === undefined || formeAvant === formeApres) return null;
  return `Les fiches déjà saisies gardent ${FORMES[formeAvant] ?? 'leur valeur'} : elles ne se liront plus comme ${FORMES[formeApres] ?? 'ce type'}.`;
}

/**
 * CE QUI EMPECHE D'ENREGISTRER LES REGLAGES D'UN CHAMP, hors formule (elle a
 * sa propre case et ses phrases, `fautesDeFormule`). Vide : tout tient.
 *
 * @param {Record<string, unknown>} champ un champ du brief @param {string} type
 * @returns {string[]}
 */
export function fautesDeReglage(champ, type) {
  const fautes = [];
  const borne = (clef, mot) => {
    const brut = texte(champ?.[clef]).trim();
    if (brut === '' || !accepteReglage(type, clef === 'chiffres' ? 'sequence' : clef)) return;
    const [min, max] = BORNES[clef];
    if (entierSaisi(brut, BORNES[clef]) === undefined) fautes.push(`${mot} : un nombre entier de ${min} à ${max}.`);
  };
  borne('decimales', 'Décimales');
  borne('max', 'Note maximale');
  borne('chiffres', 'Chiffres');
  const modele = texte(champ?.modele).trim();
  if (accepteReglage(type, 'sequence') && modele !== '' && !modeleDeNumeroValide(modele)) {
    fautes.push('Le modèle doit contenir {numéro}, la place du numéro.');
  }
  const devise = texte(champ?.devise).trim();
  if (accepteReglage(type, 'devise') && devise !== '' && !/^[A-Za-z]{3}$/.test(devise)) {
    fautes.push('La devise est un code de trois lettres (EUR, CHF, USD).');
  }
  return fautes;
}

/**
 * UN CHAMP DONT LE TYPE CHANGE PERD LES REGLAGES QUE SON NOUVEAU TYPE NE PREND
 * PAS (le catalogue) : un nombre devenu texte n'a plus d'unite, un calcul devenu
 * nombre n'a plus de formule. Le schema refuserait sinon l'ecriture ENTIERE pour
 * une incoherence que le Studio pouvait resoudre seul, comme il le fait deja
 * pour le catalogue d'un champ a choix et la cible d'un lien
 * (`champsEdition.ts`). Rend une copie ; `options`, `dependDe` et `reference`
 * ont leurs regles a eux et ne sont pas touches ici.
 *
 * @template {Record<string, unknown>} T
 * @param {T} champ un champ du fichier, avec son nouveau `type`
 * @returns {T}
 */
export function sansReglagesEtrangers(champ) {
  const sortie = { ...champ };
  for (const reglage of REGLAGES_DE_CHAMP) {
    if (reglage === 'options' || reglage === 'dependDe' || reglage === 'reference') continue;
    if (sortie[reglage] !== undefined && !accepteReglage(texte(sortie.type), reglage)) delete sortie[reglage];
  }
  return sortie;
}
